
import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import Select from "react-select";
import { useTheme } from "next-themes";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "@/components/UI/Button";
import { reactSelectNativeStyle } from "@/components/UI/reactSelect";
import {
  getFontSizeClass,
  getSelectPanelButtonHeightClass,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import tradeDataStore from "@/store/tradeDataStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import useTableUIStore from "@/store/tableUIStore";
import { getBlocksData } from "@/store/tradeBlockDataStoreACTIONS";
import {
  getEditSettingsAndAddToStore,
  patchSidebarTradeSettings,
} from "@/store/tradeSettingsStoreACTIONS";
import {
  reconcileRequestStatusesAfterSettingsUpdate,
  refreshRequestInformationForESID,
} from "@/store/requestInformationStoreACTIONS";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { isSentTradeStatus } from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/tradeWideSettingGating";

import ExpiryCountdownText from "@/components/widgets/tradeActionSummary/helper/cellRenderers/MainTableActionSummaryESLevel/ExpiresCellRenderer/ExpiryCountdownText";

import TradeSettingsCondensedGrid from "./TradeSettingsCondensedGrid";
import TradeSettingsRpView from "./TradeSettingsRpView";

// Shown in place of the editable settings when an OP trade has not been sent to
// a counterparty yet (New) or is terminal — settings can only be adjusted once
// the trade is live with a CP.
const SENT_ONLY_NOTICE =
  "Adjust the settings of active trades that have been sent to a counterparty in this panel";

// LQC-1301 (experiment) — left-rail "Trade Settings" slide-in. Mirrors the
// Historical Search tool-panel pattern: an AG Grid sidebar panel that lives on
// the same vertical rail. A Trade ID dropdown picks which OP trade to edit
// (decoupled from the blotter row selection), then a condensed settings layout
// (TradeSettingsCondensedGrid: Auto Affirm / Cover Spread + a per-CP Advise
// Cover / Advise Comp / Iceberg grid) is rendered. Apply writes through the
// existing GET hydrate -> PUT /Trade/Execution/Edit flow.

export interface ITradeIdOption {
  value: string;
  label: string;
}

// Pure: build the Trade ID dropdown options. Includes both OP and RP trades —
// the panel renders the right perspective per trade (editable OP settings vs
// read-only RP values), so an RP-perspective blotter must still be able to pick
// its trades. Exported for unit testing.
export function buildTradeIdOptions(tradeData: any[]): ITradeIdOption[] {
  if (!Array.isArray(tradeData)) return [];
  return tradeData
    .filter((t) => t?.esid)
    .map((t) => ({ value: t.esid, label: t.esid }));
}

// Hydrate the per-CP settings + baseline (getEditSettingsAndAddToStore) and
// block data (iceberg floor) the panel needs for an esid.
//
// LQC-1707: the slide-in deliberately does NOT seed the shared
// counterpartiesList slice. That slice is owned by CPList in the Edit Panel and
// holds the full universe of available CPs; the slide-in seeding it (with only
// the on-trade CPs) clobbered the Edit Panel's selection whenever both targeted
// the same trade. The slide-in's CP list is now derived directly from the
// trade's execution requests via buildCounterpartiesListFromTrade (see
// TradeSettingsCondensedGrid and patchSidebarTradeSettings).
export async function hydrateTradeForPanel(
  esid: string,
  getAccessTokenSilently: Function,
) {
  const trade = tradeDataStore
    .getState()
    .tradeData.find((t) => t.esid === esid);

  // New trades already have settings initialised by addNewTradeSettingsAtNew in
  // the Select Panel. Calling getEditSettingsAndAddToStore here would (a) set
  // editBaselineOverrides[esid], which CPSettingsGrid treats as the "edit mode"
  // flag and uses to disable autoAffirm / releaseCoverSpread, and (b) overwrite
  // any changes the user has already made in the Select Panel.
  const isNewTrade = trade?.status === ExecutionStrategyStatus.New;

  await Promise.all([
    ...(isNewTrade
      ? []
      : [getEditSettingsAndAddToStore({ esid, getAccessTokenSilently })]),
    getBlocksData({ esid, getAccessTokenSilently }),
  ]);
}

// AG Grid passes the tool panel's `toolPanelParams` (plus the grid params) as
// props. `isMiniTable` + `cryptoID` identify a trade-tile instance so the panel
// can follow that tile's trade instead of the shared blotter selection.
const TradeSettingsSidebarContent = ({
  isMiniTable,
  cryptoID,
}: {
  isMiniTable?: boolean;
  cryptoID?: string;
}) => {
  const { theme } = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const fontSizeClass = getFontSizeClass(fontSizeSetting);
  // Update / Clear follow the Select Panel trade-creation buttons' height across
  // the size settings (just the height — width/text unchanged) (LQC-1830).
  const updateClearHeight = getSelectPanelButtonHeightClass(fontSizeSetting);

  const tradeData = tradeDataStore((state) => state.tradeData);

  // On the main blotter the selection is keyed by cryptoID (dropdown / gear
  // icon write to panelSelectedEsidByCryptoId). In a trade tile the panel
  // follows that tile's own trade, kept in sync by ActionSummaryTable and
  // stored in miniTilePanelEsidByCryptoId.
  const globalSelectedEsid = tradeSettingsStore((state) =>
    cryptoID ? (state.panelSelectedEsidByCryptoId[cryptoID] ?? null) : null,
  );
  const miniTileEsid = tradeSettingsStore((state) =>
    isMiniTable && cryptoID
      ? (state.miniTilePanelEsidByCryptoId[cryptoID] ?? null)
      : null,
  );
  const setPanelSelectedEsid = tradeSettingsStore(
    (state) => state.setPanelSelectedEsid,
  );
  const selectedEsid = isMiniTable ? miniTileEsid : globalSelectedEsid;
  const [opRefreshedEsid, setOpRefreshedEsid] = useState<string | null>(null);
  // The esid whose request-information refresh has completed. The RP view waits
  // until this matches the selected trade so its negotiation statuses don't flash
  // from stale to fresh on open.
  const [rpRefreshedEsid, setRpRefreshedEsid] = useState<string | null>(null);

  const tradeIdOptions: ITradeIdOption[] = useMemo(
    () => buildTradeIdOptions(tradeData),
    [tradeData],
  );

  const selectedTrade = tradeData.find((t) => t.esid === selectedEsid);

  // Perspective + send-state drive which view renders:
  //   OP + sent      -> editable condensed settings grid
  //   OP + not sent  -> "sent only" notice
  //   RP + Order     -> "no settings" message
  //   RP + RFQ       -> read-only view from blotter trade data (no hydration)
  const isOp = selectedTrade?.role === "OP";
  const isOrder = selectedTrade?.type === "Order";
  const isSent = isSentTradeStatus(selectedTrade?.status);
  // Only OP + sent trades pull settings from the Edit/Settings endpoint.
  const needsHydration = !!selectedEsid && isOp && isSent;
  // RP RFQs pull the latest request-information state on open (see effect below).
  // The app-start bulk load only covers OP trades, so without this the RP's
  // negotiation statuses (Requested / Released / Rejected) would be stale until a
  // SignalR push lands. Orders have no settings/requests, so they're excluded.
  const needsRequestInfoRefresh =
    !!selectedEsid && !!selectedTrade && !isOp && !isOrder;
  // OP and RP view is ready to render once this trade's request info has been refreshed.
  const rpReady = rpRefreshedEsid === selectedEsid;
  const opReady = opRefreshedEsid === selectedEsid;

  // Render the grid only once the settings store has hydrated this trade —
  // the condensed grid assumes settings exist for the esid.
  const selectedSettings = tradeSettingsStore((state) =>
    selectedEsid
      ? state.tradeSettingsData.find((t) => t.esid === selectedEsid)?.settings
      : undefined,
  );

  // Hydrate whenever the selected trade changes — covers both the dropdown and
  // an external retarget from the blotter gear icon. RP and not-yet-sent OP
  // trades don't hit the settings endpoint.
  useEffect(() => {
    if (!needsHydration || !selectedEsid) return;
    let active = true;
    setOpRefreshedEsid(null);
    hydrateTradeForPanel(selectedEsid, getAccessTokenSilently).finally(() => {
      if (active) setOpRefreshedEsid(selectedEsid);
    });
    return () => {
      active = false;
    };
    // getAccessTokenSilently is intentionally omitted: useAuth0 can hand back a
    // fresh reference on re-render, which would re-trigger hydration and leave
    // the loading flag stuck. selectedEsid is the only real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEsid, needsHydration]);

  // RP: refresh this trade's request-information state when the panel opens or
  // retargets onto it, marking the esid ready when it lands so the view can hold
  // until then (no stale→fresh flash). `finally` runs on error too, so a failed
  // refresh still releases the view rather than blocking on "Loading…" forever.
  // (getAccessTokenSilently omitted from deps for the same reason as above.)
  useEffect(() => {
    if (!needsRequestInfoRefresh || !selectedEsid) return;
    let active = true;
    setRpRefreshedEsid(null);
    refreshRequestInformationForESID(
      selectedEsid,
      getAccessTokenSilently,
    ).finally(() => {
      if (active) setRpRefreshedEsid(selectedEsid);
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEsid, needsRequestInfoRefresh]);

  const applyChanges = async () => {
    if (!selectedEsid) return;
    // Submit the settings, then reconcile the request-information status from the
    // SAME submitted payloads (LQC-1842/1843): a released field clears a prior
    // Reject / accepts an outstanding request; a withdrawn field flips Released →
    // Rejected. Reading status here is correct — the settings PATCH never touches
    // the request store, so it's still the pre-change value.
    const submitted = await patchSidebarTradeSettings({
      esid: selectedEsid,
      getAccessTokenSilently,
    });
    await reconcileRequestStatusesAfterSettingsUpdate({
      esid: selectedEsid,
      payloads: submitted,
      getAccessTokenSilently,
    });
  };

  const clearSelection = () => cryptoID && setPanelSelectedEsid(cryptoID, null);

  return (
    <div
      data-testid="TradeSettingsSidebarContainer"
      id="TradeSettingsSidebarContainer"
      // min-w-0: AG Grid's .ag-tool-panel-wrapper is a flex row with
      // overflow-x hidden, so without this the panel stretches to the CP grid's
      // min-content width and clips instead of letting the grid scroll (LQC-2271).
      className={`m-2 flex min-w-0 flex-col p-1 ${fontSizeClass}`}
    >
      <div className="mb-2 pl-1 text-sm font-semibold text-gray-600 dark:text-gray-400">
        Trade Settings
      </div>

      {/* Trade ID picker — main blotter only. In a trade tile the panel is
          bound to the tile's own trade, so the picker is hidden. */}
      {!isMiniTable && (
        <>
          <label className="pb-1 text-gray-500 dark:text-gray-400">
            Trade ID:
          </label>
          <div className="w-fit min-w-[120px]">
            <Select
              inputId="TradeSettingsTradeIdSelect"
              aria-label="Trade ID"
              options={tradeIdOptions}
              value={
                tradeIdOptions.find((o) => o.value === selectedEsid) ?? null
              }
              onChange={(option) =>
                cryptoID &&
                setPanelSelectedEsid(
                  cryptoID,
                  (option as ITradeIdOption | null)?.value ?? null,
                )
              }
              placeholder="Select a trade…"
              isClearable
              styles={reactSelectNativeStyle(theme)}
            />
          </div>
        </>
      )}

      {!selectedEsid && (
        <div
          data-testid="TradeSettingsEmptyState"
          className="mt-4 text-gray-500 dark:text-gray-400"
        >
          {isMiniTable
            ? "Select a trade in this tile to view its settings."
            : "Select a Trade ID to view and edit its settings."}
        </div>
      )}

      {/* LQC-2678 - read-only time remaining, above the perspective-specific
          views so the OP and the RP both get it from one implementation.
          Editing is the OP's alone and stays on the Expires column's pencil /
          right-click. */}
      {selectedEsid && selectedTrade && (
        <div
          data-testid="TradeSettingsExpiresRow"
          id="TradeSettingsExpiresRow"
          className="mt-3 flex items-center justify-between gap-2 border-b border-gray-200 pb-2 dark:border-gray-700"
        >
          <span className="whitespace-nowrap text-gray-600 dark:text-gray-300">
            Expires
          </span>
          <ExpiryCountdownText
            esid={selectedEsid}
            testId="TradeSettingsExpiresValue"
          />
        </div>
      )}

      {/* RP perspective: read-only values for RFQs, or message for Orders. */}
      {selectedEsid && selectedTrade && !isOp && isOrder && (
        <div
          data-testid="TradeSettingsRpOrderNoSettings"
          className="mt-4 text-gray-500 dark:text-gray-400"
        >
          Orders do not have settings to view.
        </div>
      )}

      {/* RP RFQ: hold the view until this trade's latest request-information
          state has loaded, so the negotiation statuses don't flash from stale to
          fresh on open. */}
      {selectedEsid && selectedTrade && !isOp && !isOrder && !rpReady && (
        <div
          data-testid="TradeSettingsRpLoading"
          className="mt-4 text-gray-500 dark:text-gray-400"
        >
          Loading…
        </div>
      )}

      {selectedEsid && selectedTrade && !isOp && !isOrder && rpReady && (
        <div className="mt-3">
          <TradeSettingsRpView trade={selectedTrade} />
        </div>
      )}

      {/* OP, not yet sent (New) or terminal: settings aren't adjustable. */}
      {selectedEsid && selectedTrade && isOp && !isSent && (
        <div
          data-testid="TradeSettingsSentOnlyNotice"
          className="mt-4 text-gray-500 dark:text-gray-400"
        >
          {SENT_ONLY_NOTICE}
        </div>
      )}

      {selectedEsid && selectedTrade && isOp && isSent && !opReady && (
        <div
          data-testid="TradeSettingsLoading"
          className="mt-4 text-gray-500 dark:text-gray-400"
        >
          Loading settings…
        </div>
      )}

      {selectedEsid && selectedTrade && isOp && isSent && opReady && selectedSettings && (
        <div className="mt-3 flex flex-col gap-2">
          <TradeSettingsCondensedGrid
            esid={selectedEsid}
            data={selectedTrade}
          />
          {/* Height tracks the Select Panel trade-creation buttons across the
              size settings; min-w + px keep them compact (LQC-1830). */}
          <div className="mt-2 flex w-full space-x-2">
            <Button
              type="button"
              variant={"mainOrangeAction"}
              data-testid="TradeSettingsApplyButton"
              className={clsx(updateClearHeight, "min-w-[6rem] flex-none px-3")}
              onClick={applyChanges}
            >
              Update
            </Button>
            {/* Clear deselects the trade — only meaningful on the blotter,
                where the user picked it. In a tile the trade is fixed. */}
            {!isMiniTable && (
              <Button
                type="button"
                variant={"secondaryBlueAction"}
                data-testid="TradeSettingsClearButton"
                className={clsx(updateClearHeight, "min-w-[6rem] flex-none px-3")}
                onClick={clearSelection}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeSettingsSidebarContent;
